<template>
  <div
    ref="triggerRef"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    class="tooltip-wrapper"
  >
    <slot />
    <Teleport to="body">
      <Transition name="tooltip-fade">
        <div
          v-if="visible"
          ref="tooltipRef"
          :style="tooltipStyle"
          class="tooltip-popper"
        >
          <div class="tooltip-content">
            <slot name="content">
              {{ content }}
            </slot>
          </div>
          <div
            class="tooltip-arrow"
            :class="`arrow-${placement}`"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const emits = defineEmits(['show', 'hide'])

const props = defineProps({
  content: {
    type: String,
    default: ''
  },
  placement: {
    type: String,
    default: 'bottom',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  openDelay: {
    type: Number,
    default: 0
  },
  offset: {
    type: Number,
    default: 12
  },
  trigger: {
    type: String,
    default: 'hover',
    validator: (value) => ['hover', 'click'].includes(value)
  }
})

const triggerRef = ref(null)
const tooltipRef = ref(null)
const visible = ref(false)
const tooltipStyle = ref({})

let showTimer = null
let hideTimer = null

const calculatePosition = () => {
  if (!triggerRef.value || !tooltipRef.value) return

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const tooltipRect = tooltipRef.value.getBoundingClientRect()

  const placement = props.placement
  const offset = props.offset || 0

  let top = 0
  let left = 0

  switch (placement) {
    case 'top':
      top = triggerRect.top - tooltipRect.height - offset
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      break
    case 'bottom':
      top = triggerRect.top + triggerRect.height + offset
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
      break
    case 'left':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.left - tooltipRect.width - props.offset
      break
    case 'right':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
      left = triggerRect.right + props.offset
      break
  }

  // 边界检测和调整
  if (left < 0) {
    left = 8
  } else if (left + tooltipRect.width > innerWidth) {
    left = innerWidth - tooltipRect.width - 8
  }

  if (top < 0) {
    top = 8
  } else if (top + tooltipRect.height > innerHeight) {
    top = innerHeight - tooltipRect.height - 8
  }

  tooltipStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 9999
  }
}

const handleClick = () => {
  if (props.disabled || props.trigger !== 'click') return

  if (visible.value) {
    hideTooltip()
  } else {
    showTooltip()
  }
}

const showTooltip = () => {
  if (props.disabled) return

  clearTimeout(hideTimer)
  if (props.openDelay > 0) {
    showTimer = setTimeout(() => {
      visible.value = true
      emits('show')
      nextTick(calculatePosition)
    }, props.openDelay)
  } else {
    visible.value = true
    emits('show')
    nextTick(calculatePosition)
  }
}

const hideTooltip = () => {
  if (props.disabled) return

  clearTimeout(showTimer)

  if (props.openDelay > 0) {
    hideTimer = setTimeout(() => {
      visible.value = false
      emits('hide')
    }, props.openDelay)
  } else {
    visible.value = false
    emits('hide')
  }
}

const handleMouseEnter = () => {
  if (props.trigger === 'click') return
  showTooltip()
}

const handleMouseLeave = () => {
  if (props.trigger === 'click') return
  hideTooltip()
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const handleDocumentClick = (e) => {
  if (
    props.trigger === 'click' &&
    visible.value &&
    !triggerRef.value.contains(e.target) &&
    !tooltipRef.value?.contains(e.target)
  ) {
    hideTooltip()
  }
}

</script>

<style lang='less'>
.tooltip-wrapper {
  display: inline-block;
}

.tooltip-popper {
  display: inline-block;
  .tooltip-content {
    padding: 6px 12px;
    max-width: 400px;
    background-color: #141414;
    border-radius: 4px;
    color: white;
    font-size: 10px;
    font-family: mon-500;
    line-height: 14px;
  }

  .tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0%;

    &.arrow-top {
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      border-top: 4px solid #141414;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
    }

    &.arrow-bottom {
      top: -4px;
      left: 50%;
      transform: translateX(-50%);
      border-bottom: 4px solid #141414;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
    }

    &.arrow-left {
      right: -4px;
      top: 50%;
      transform: translateY(-50%);
      border-left: 4px solid #141414;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
    }

    &.arrow-right {
      left: -4px;
      top: 50%;
      transform: translateY(-50%);
      border-right: 4px solid #141414;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
    }
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.3s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
